import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Info, Megaphone, X } from 'lucide-react';
import { useState } from 'react';
import { api } from '@/api/client';

export interface NoticeItem {
  id: number;
  message: string;
  severity: 'INFO' | 'WARNING' | 'CRITICAL';
  linkUrl?: string | null;
}

const TONE = {
  INFO:     { bar: 'bg-sky-50 border-sky-200 text-sky-900',       icon: 'text-sky-600',    Icon: Info },
  WARNING:  { bar: 'bg-amber-50 border-amber-200 text-amber-900', icon: 'text-amber-600',  Icon: AlertTriangle },
  CRITICAL: { bar: 'bg-rose-50 border-rose-200 text-rose-900',    icon: 'text-rose-600',   Icon: AlertTriangle },
} as const;

/**
 * The scrolling notice bar itself. Pure presentation — the admin preview and the merchant portal
 * both render THIS, so what an admin previews is literally what merchants get.
 *
 * The track is duplicated and translated -50%, which is what makes the scroll seamless: the copy
 * slides in exactly as the original slides out, with no gap or jump at the wrap.
 */
export function MerchantNoticeMarquee({ items, onDismiss }: { items: NoticeItem[]; onDismiss?: () => void }) {
  if (!items?.length) return null;

  // One bar, worst severity wins — three stacked bars would push the page down and get ignored.
  const severity = items.some((i) => i.severity === 'CRITICAL') ? 'CRITICAL'
    : items.some((i) => i.severity === 'WARNING') ? 'WARNING' : 'INFO';
  const tone = TONE[severity];
  const Icon = tone.Icon;

  // Longer text needs proportionally longer to cross, or a long notice becomes unreadably fast.
  const chars = items.reduce((n, i) => n + i.message.length + 12, 0);
  const duration = Math.max(18, Math.min(120, Math.round(chars / 6)));

  const track = (
    <span className="inline-flex shrink-0 items-center" aria-hidden={false}>
      {items.map((n) => (
        <span key={n.id} className="inline-flex items-center gap-2 px-6">
          <Megaphone size={13} className={`${tone.icon} shrink-0`} />
          <span>{n.message}</span>
          {n.linkUrl && (
            <a href={n.linkUrl} target="_blank" rel="noreferrer" className="underline underline-offset-2 font-medium hover:opacity-80">
              Details
            </a>
          )}
        </span>
      ))}
    </span>
  );

  return (
    <div className={`border rounded-lg flex items-center gap-2 pl-3 pr-2 py-2 overflow-hidden ${tone.bar}`}>
      <Icon size={16} className={`${tone.icon} shrink-0`} />
      {/* group/hover pauses the scroll so a merchant can actually read a long notice or click it. */}
      <div className="flex-1 min-w-0 overflow-hidden group">
        <div
          className="inline-flex whitespace-nowrap text-sm will-change-transform group-hover:[animation-play-state:paused] motion-reduce:animate-none"
          style={{ animation: `gaint-marquee ${duration}s linear infinite` }}
        >
          {track}
          {/* Duplicate makes the loop seamless; hidden from screen readers so the text is not
              announced twice. */}
          <span aria-hidden="true" className="inline-flex shrink-0 items-center">{track}</span>
        </div>
      </div>
      {onDismiss && (
        <button onClick={onDismiss} title="Hide until next login" className="p-1 rounded hover:bg-black/5 shrink-0">
          <X size={14} className={tone.icon} />
        </button>
      )}
      <style>{`@keyframes gaint-marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }`}</style>
    </div>
  );
}

/**
 * Live version for the merchant portal — fetches what this merchant should see and renders it.
 * Polls every 2 minutes so a notice raised mid-shift appears without the merchant reloading, which
 * is the entire point of a "service interrupted right now" banner.
 *
 * Dismissal is per browser session (sessionStorage), NOT permanent: an outage notice the merchant
 * closed in the morning must come back tomorrow if it is still running.
 */
export function MerchantNoticeBar() {
  const [dismissed, setDismissed] = useState<number[]>(() => {
    try { return JSON.parse(sessionStorage.getItem('dismissedNotices') ?? '[]'); } catch { return []; }
  });

  const { data } = useQuery({
    queryKey: ['merchant-notices', 'active'],
    queryFn: async () => (await api.get<{ items: NoticeItem[] }>('/merchant-notices/active')).data,
    refetchInterval: 120_000,
    refetchOnWindowFocus: true,
    // A failed notice fetch must never break the page it sits on top of.
    retry: 1,
  });

  const items = (data?.items ?? []).filter((n) => !dismissed.includes(n.id));
  if (!items.length) return null;

  return (
    <div className="px-6 pt-3">
      <MerchantNoticeMarquee
        items={items}
        onDismiss={() => {
          const next = Array.from(new Set([...dismissed, ...items.map((i) => i.id)]));
          setDismissed(next);
          try { sessionStorage.setItem('dismissedNotices', JSON.stringify(next)); } catch { /* private mode — fine */ }
        }}
      />
    </div>
  );
}
